import { ArrowUpRight, MousePointer2 } from "lucide-react";
import { proxied } from "@/lib/api";

const ExhibitionHero = ({ settings = {} }) => {
  const link = settings.exhibition_hero_link || "https://rudralife.com/exhibition/";
  return (
    <section id="exhibition-hero" className="bg-[#FAF6F0] py-14 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        {/* Top Header — Combo Kits style typography */}
        <div className="mb-8 md:mb-14 text-center">
          <div className="text-[#8B2323] text-[10px] md:text-sm tracking-[0.4em] md:tracking-[0.5em] uppercase mb-3">
            {settings.exhibition_top_kicker || "Begin Your Spiritual Journey In Person"}
          </div>
          <h2 className="font-serif text-3xl md:text-6xl italic tracking-tight text-[#C89838] leading-tight">
            {settings.exhibition_top_title_line1 || "25 Years of One-on-One"}
            <br className="hidden md:block" /> {settings.exhibition_top_title_line2 || "Guidance & Trust"}
          </h2>
          <div className="h-[2px] w-16 bg-[#D4AF37] mt-5 mx-auto" />
        </div>

        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          data-testid="exhibition-hero-card"
          data-cursor="button"
          className="group relative block rounded-[2rem] bg-[#1A1412]"
        >
          <div className="relative aspect-[16/10] md:aspect-[21/9] rounded-[2rem] overflow-visible">
            <div className="absolute inset-0 rounded-[2rem] overflow-hidden">
              <img
                src={proxied(settings.exhibition_hero_image)}
                alt="Rudralife Exhibition"
                className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity duration-700 group-hover:scale-105 transform-gpu"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A1412]/85 via-[#1A1412]/35 to-transparent" />
            </div>

            {/* 3D pop-out mala on the right — bottom slightly overflows the banner frame */}
            {settings.exhibition_mala_image && (
              <img
                src={proxied(settings.exhibition_mala_image)}
                alt="Sacred mala"
                aria-hidden="true"
                className="hidden md:block absolute right-6 md:right-14 top-8 md:top-4 h-[130%] w-auto object-contain pointer-events-none drop-shadow-[0_35px_45px_rgba(0,0,0,0.55)] z-10 transition-transform duration-700 group-hover:-translate-y-2"
                draggable={false}
                style={{ transformOrigin: "center top" }}
              />
            )}

            <div className="absolute inset-0 flex items-center z-20">
              <div className="max-w-3xl px-5 md:px-16 text-white">
                <div className="text-[#D4AF37] text-[9px] md:text-xs tracking-[0.5em] uppercase mb-2 md:mb-4">
                  Namaste
                </div>
                <h2 className="font-serif text-2xl leading-[1.1] tracking-tight sm:text-3xl md:text-6xl lg:text-7xl">
                  {settings.exhibition_hero_title || "Welcome To Rudralife Exhibition"}
                </h2>
                <p className="mt-2.5 md:mt-5 text-xs sm:text-sm md:text-2xl font-serif italic text-[#D4AF37] leading-snug">
                  {settings.exhibition_hero_subtitle || "The Largest Rudraksha Exhibition in the World"}
                </p>
                <div className="mt-4 md:mt-8 inline-flex items-center gap-2 md:gap-3 text-[#F5EEDD] text-[9px] md:text-xs tracking-[0.3em] md:tracking-[0.35em] uppercase border-b border-[#D4AF37]/40 pb-1.5 md:pb-2 group-hover:border-[#D4AF37] transition-colors">
                  <MousePointer2 size={12} className="text-[#D4AF37]" />
                  <span className="hidden sm:inline">Click Photo to Visit Exhibition Page</span>
                  <span className="sm:hidden">Tap to visit</span>
                  <ArrowUpRight size={12} className="text-[#D4AF37] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* Corner arrow badge */}
            <div className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 md:w-16 md:h-16 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1A1412] group-hover:scale-110 transition-transform z-30">
              <ArrowUpRight size={20} />
            </div>
          </div>
        </a>

        {/* Exhibition content paragraph (admin controlled) */}
        {settings.exhibition_content && (
          <div className="max-w-4xl mx-auto mt-8 md:mt-12 px-4 md:px-8 text-center">
            <p className="text-sm md:text-lg text-[#2D2825] leading-relaxed whitespace-pre-line">
              {settings.exhibition_content}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExhibitionHero;
